import { IServerError, IServerResponse } from "@/api/types";

/**
 * Менеджер живёт на уровне мастер-группы: он один на мастера и всех его слейвов.
 * На нём завязаны баланс, холд и то, каких партнёров видит сотрудник.
 */
export const MANAGER_SLAVE_HINT =
    'Менеджер слейва совпадает с менеджером мастер-аккаунта. Смените менеджера у мастера - он изменится у всех его слейвов.';

export const MANAGER_SLAVE_BULK_HINT =
    'Среди выбранных партнёров есть слейвы. Менеджер слейва совпадает с менеджером мастер-аккаунта - смените его у мастера.';

export const MANAGER_FROM_MASTER_HINT =
    'Менеджер взят у мастер-аккаунта: у слейва он всегда совпадает с менеджером мастера.';

export const MASTER_WITHOUT_MANAGER_HINT =
    'У мастер-аккаунта не указан менеджер, наследовать слейву нечего. Сначала назначьте менеджера мастеру.';

export const MANAGER_SLAVE_REJECTED =
    'Менеджер не изменён: у слейва он совпадает с менеджером мастер-аккаунта. Смените менеджера у мастера.';

export const MASTER_WITHOUT_MANAGER_REJECTED =
    'У мастер-аккаунта не указан менеджер, а слейв наследует его. Сначала назначьте менеджера мастеру.';

interface IMasterLink {
    id?: number | null;
    master_id?: number | null;
}

/**
 * Слейв - партнёр, привязанный к чужому мастер-аккаунту.
 *
 * Данных о мастере в ответе нет - считаем слейвом. Смена менеджера у слейва ломает баланс,
 * холд и видимость партнёра, поэтому неизвестность блокирует действие, а не разрешает его:
 * иначе набор колонок в таблице решал бы, работает защита или нет.
 */
export function isSlaveAffiliate(affiliate: IMasterLink): boolean {
    if (affiliate.master_id === undefined || affiliate.id === undefined) return true;
    return !!affiliate.master_id && affiliate.master_id !== affiliate.id;
}

/**
 * users-service отдаёт ValidationException как 400, а причину кладёт в details, не в message:
 * слейв в запросе - ключ `accounts`, мастер без менеджера - ключ `master_id` (users-service!731).
 */
function getValidationDetail(err: IServerResponse<IServerError>, key: string): string {
    if (err?.status !== 400) return '';
    return String(err?.data?.error?.details?.[key] ?? '');
}

export function isSlaveManagerRejection(err: IServerResponse<IServerError>): boolean {
    return /slave/i.test(getValidationDetail(err, 'accounts'));
}

export function isMasterWithoutManagerRejection(err: IServerResponse<IServerError>): boolean {
    return /has no manager/i.test(getValidationDetail(err, 'master_id'));
}

/**
 * Подменяет техническое сообщение сервера понятным, остальные ошибки отдаёт как есть.
 * details убираются: в них лежит тот же текст на английском, который дублировался бы под сообщением.
 */
export function withManagerRejectionMessage(
    err: IServerResponse<IServerError>,
): IServerResponse<IServerError> {
    let message = '';
    if (isSlaveManagerRejection(err)) message = MANAGER_SLAVE_REJECTED;
    if (isMasterWithoutManagerRejection(err)) message = MASTER_WITHOUT_MANAGER_REJECTED;
    if (!message) return err;

    return { ...err, data: { ...err.data, error: { message, details: {} } } };
}
